import * as s from '~js/utils/selectors';
import * as a from '~js/utils/attributes';
import * as c from '~js/utils/classes';
import { QuickViewPopup } from '~js/components/quick-view-popup';
import { Tooltip } from '~js/components/tooltip';
import {
  debounce,
} from '~js/utils/utils';

const Flickity = themeVendor.Flickity;

export class Look {
  constructor(container) {
    this.container = container;
    this.slider = this.container.querySelector(s.selectors$h.slider);
    this.slides = this.container.querySelectorAll(s.selectors$h.sliderItem);
    this.pointers = this.container.querySelectorAll(s.selectors$h.pointer);
    this.flkty = null;
    this.observer = null;

    this.checkSlidesSizeOnResize = () => this.checkSlidesSize();
    this.resizeSliderEvent = (event) => this.resizeSlider(event);
    this.pointersInit = (event) => this.dotPointers(event);
    this.pointersOver = (event) => this.dotPointerIn(event);
    this.pointersOut = (event) => this.dotPointerOut(event);

    this.debouncedBlockSelectCallback = debounce((event) => this.debouncedBlockSelect(event), 500);

    this.quickViewPopup = new QuickViewPopup(this.container);
    this.listen();
  }

  listen() {
    if (this.slider) {
      this.checkSlidesSize();
      document.addEventListener('theme:resize:width', this.checkSlidesSizeOnResize);
      this.slider.addEventListener('theme:slider:resize', this.resizeSliderEvent);
    }

    if (this.pointers.length > 0) {
      this.pointers.forEach((pointer) => {
        pointer.addEventListener('click', this.pointersInit);
        pointer.addEventListener('mouseover', this.pointersOver);
        pointer.addEventListener('mouseleave', this.pointersOut);
      });
    }
  }

  checkSlidesSize() {
    const isDesktop = window.innerWidth >= theme.sizes.small;

    this.initTooltips();

    if (isDesktop) {
      if (this.slides.length > 2) {
        this.initSlider();
      } else {
        this.destroySlider();
        this.slidesTabIndex();
      }

      return;
    }

    if (!isDesktop && this.slides.length > 1) {
      this.initSlider();

      return;
    }

    this.destroySlider();
  }

  initTooltips() {
    this.tooltips = this.container.querySelectorAll(s.selectors$h.tooltip);
    this.tooltips.forEach((tooltip) => {
      new Tooltip(tooltip);
    });
  }

  initSlider() {
    if (this.flkty === null) {
      this.flkty = new Flickity(this.slider, {
        prevNextButtons: true,
        wrapAround: true,
        adaptiveHeight: false,
        cellAlign: 'left',
        groupCells: false,
        contain: true,
        on: {
          ready: () => {
            this.slidesTabIndex();
            this.setSliderArrowsPosition();
            this.dotPointers();
          },
          change: () => {
            this.slidesTabIndex();
            this.dotPointers();
          },
        },
      });

      return;
    }

    this.setSliderArrowsPosition();
  }

  setSliderArrowsPosition() {
    const isDesktop = window.innerWidth >= theme.sizes.small;

    if (!isDesktop) return;

    const arrows = this.slider.querySelectorAll(s.selectors$h.flickityButton);
    const image = this.slider.querySelector(s.selectors$h.productGridItemImage);

    if (arrows.length && image) {
      arrows.forEach((arrow) => {
        arrow.style.top = `${image.offsetHeight / 2}px`;
      });
    }
  }

  slidesTabIndex() {
    if (this.slides.length < 3) {
      this.slider.querySelectorAll(s.selectors$h.links).forEach((link) => {
        link.setAttribute(a.attributes$d.tabIndex, '0');
      });

      return;
    }

    const slider = Flickity.data(this.slider);

    slider.cells.forEach((slide) => {
      let tabIndex = '-1';
      if (slide.element.classList.contains(c.classes$d.isSelected)) {
        tabIndex = '0';
      }

      slide.element.querySelectorAll(s.selectors$h.links).forEach((link) => {
        link.setAttribute(a.attributes$d.tabIndex, tabIndex);
      });
    });
  }

  destroySlider() {
    if (typeof this.flkty === 'object' && this.flkty !== null) {
      this.flkty.destroy();
      this.flkty = null;
    }
  }

  resizeSlider(event) {
    const slider = event.target;
    const flkty = Flickity.data(slider) || null;

    if (!flkty) return;
    flkty.resize();
  }

  dotPointers(event) {
    if (this.pointers.length === 0) return;

    this.pointers.forEach((button) => {
      button.classList.remove(c.classes$d.pointerSelected);
    });

    if (event) {
      const dotIndex = event.target.getAttribute(a.attributes$d.pointer);

      this.flkty?.select(dotIndex);

      return;
    }

    const slideIndex = this.flkty == null ? 0 : this.flkty.selectedIndex;

    if (slideIndex >= 0) {
      this.pointers[slideIndex].classList.add(c.classes$d.pointerSelected);
    }
  }

  dotPointerIn(event) {
    const dotIndex = event.target.getAttribute(a.attributes$d.pointer);
    const image = this.slides[dotIndex].querySelector(s.selectors$h.productGridItemImage);
    const isTouch = matchMedia('(pointer:coarse)').matches;
    const isMobile = window.innerWidth < theme.sizes.small;
    if (!isMobile && !isTouch) {
      this.observeImage(image);
    }

    this.pointers.forEach((pointer) => {
      pointer.style.setProperty('--look-animation', 'none');
    });
  }

  dotPointerOut(event) {
    const dotIndex = event.target.getAttribute(a.attributes$d.pointer);
    const image = this.slides[dotIndex].querySelector(s.selectors$h.productGridItemImage);
    image.classList.remove(c.classes$d.productGridItemImageHover);
    image.dispatchEvent(new Event('mouseleave'));
    if (this.observer) {
      this.observer.disconnect();
    }

    this.pointers.forEach((pointer) => {
      pointer.style.removeProperty('--look-animation');
    });
  }

  observeImage(image) {
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          const target = entry.target;
          const outsideWrapper = entry.intersectionRatio == 0;

          if (!outsideWrapper) {
            target.dispatchEvent(new Event('mouseenter'));
            target.classList.add(c.classes$d.productGridItemImageHover);
          }
        });
      },
      {
        root: this.slider,
        threshold: [0.95, 1],
      }
    );
    this.observer.observe(image);
  }

  triggerClick(target) {
    requestAnimationFrame(() => target.dispatchEvent(new Event('click')));
  }

  destroyQuickViewPopup() {
    const pswpElement = this.quickViewPopup?.loadPhotoswipe?.pswpElement;
    if (!pswpElement) return;
    if (pswpElement.classList.contains(c.classes$d.popupOpen)) {
      this.quickViewPopup.loadPhotoswipe.popup.close();
    }
  }

  /**
   * Event callback for Theme Editor `shopify:block:select` event
   * The timeouts here are necessary for issues with selecting blocks from one `Shop the look` section to another
   */
  onBlockSelect(event) {
    this.debouncedBlockSelectCallback(event);
  }

  debouncedBlockSelect(event) {
    const pswpElement = this.quickViewPopup?.loadPhotoswipe?.pswpElement;

    // No popup element
    if (!pswpElement) {
      setTimeout(() => this.triggerClick(event.target), 400);
      return;
    }

    setTimeout(() => {
      // Popup initialized
      if (pswpElement.classList.contains(c.classes$d.popupOpen)) {
        // Popup opened
        const holder = this.quickViewPopup.loadPhotoswipe.pswpElement.querySelector(
          `[${a.attributes$d.hotspot}="${event.target.getAttribute(a.attributes$d.hotspot)}"]`
        );
        const quickViewItemHolders = this.quickViewPopup.loadPhotoswipe.pswpElement.querySelectorAll(
          s.selectors$h.quickViewItemHolder
        );

        holder.classList.add(c.classes$d.isActive);

        quickViewItemHolders.forEach((element) => {
          if (element !== holder) {
            element.classList.remove(c.classes$d.isActive);
          }
        });
      } else {
        // Popup closed
        this.triggerClick(event.target);
      }
    });
  }

  /**
   * Event callback for Theme Editor `shopify:section:unload` event
   */
  onUnload() {
    this.destroyQuickViewPopup();
    document.removeEventListener('theme:resize:width', this.checkSlidesSizeOnResize);

    if (this.slider) {
      this.slider.removeEventListener('theme:slider:resize', this.resizeSliderEvent);
    }
  }

  /**
   * Event callback for Theme Editor `shopify:section:deselect` event
   */
  onDeselect() {
    this.destroyQuickViewPopup();
  }
}